/**
 * Package key value object.
 *
 * Single-section: section_day_season_services_capacityMin_capacityMax
 * Both-section:   both_day_season_services_menMin_menMax_womenMin_womenMax
 *
 * Split ranges are part of a `both` key so equal total ranges with different
 * men/women allocations cannot collide.
 */

import type {
  SectionModeValue,
  DayTypeValue,
  SeasonValue,
} from './PackageEnums';
import {
  DayType,
  Season,
  SectionMode,
} from './PackageEnums';
import { ValidationError } from '@domain/errors/DomainErrors';

interface PackageKeyIdentity {
  sectionMode: SectionModeValue;
  dayType: DayTypeValue;
  season: SeasonValue;
  hasIncludedServices: boolean;
}

export interface PackageKeyParts extends PackageKeyIdentity {
  capacityMin: number;
  capacityMax: number;
  capacityMenMin?: number;
  capacityMenMax?: number;
  capacityWomenMin?: number;
  capacityWomenMax?: number;
}

export type ParsedPackageKeyParts = PackageKeyParts;

export class PackageKey {
  private constructor(public readonly value: string) {}

  static build(parts: PackageKeyParts): PackageKey {
    assertRange(parts.capacityMin, parts.capacityMax, 'capacity');
    const services = parts.hasIncludedServices ? 'yes' : 'no';
    const prefix =
      `${parts.sectionMode}_${parts.dayType}_${parts.season}_${services}`;

    if (parts.sectionMode !== SectionMode.BOTH) {
      return new PackageKey(
        `${prefix}_${parts.capacityMin}_${parts.capacityMax}`
      );
    }

    const menMin = requireRangePart(parts.capacityMenMin, 'capacityMenMin');
    const menMax = requireRangePart(parts.capacityMenMax, 'capacityMenMax');
    const womenMin = requireRangePart(
      parts.capacityWomenMin,
      'capacityWomenMin'
    );
    const womenMax = requireRangePart(
      parts.capacityWomenMax,
      'capacityWomenMax'
    );
    assertRange(menMin, menMax, 'capacityMen');
    assertRange(womenMin, womenMax, 'capacityWomen');
    if (
      parts.capacityMin !== menMin + womenMin ||
      parts.capacityMax !== menMax + womenMax
    ) {
      throw new ValidationError(
        'نطاق السعة الإجمالي يجب أن يساوي مجموع نطاقي الرجال والنساء',
        { field: 'capacityMin', value: parts.capacityMin }
      );
    }

    return new PackageKey(
      `${prefix}_${menMin}_${menMax}_${womenMin}_${womenMax}`
    );
  }

  static parse(key: string): ParsedPackageKeyParts {
    const parts = key.split('_');
    if (parts.length !== 6 && parts.length !== 8) throw invalidKey(key);

    const [sectionMode, dayType, season, services] = parts;
    if (services !== 'yes' && services !== 'no') throw invalidKey(key);
    if (
      sectionMode !== SectionMode.BOTH &&
      sectionMode !== SectionMode.MEN &&
      sectionMode !== SectionMode.WOMEN
    ) {
      throw invalidKey(key);
    }
    if (
      dayType !== DayType.ALL &&
      dayType !== DayType.WEEKDAY &&
      dayType !== DayType.WEEKEND
    ) {
      throw invalidKey(key);
    }
    if (
      season !== Season.ALL &&
      season !== Season.SCHOOL &&
      season !== Season.SUMMER &&
      season !== Season.HOLIDAY &&
      season !== Season.EID
    ) {
      throw invalidKey(key);
    }

    const identity: PackageKeyIdentity = {
      sectionMode,
      dayType,
      season,
      hasIncludedServices: services === 'yes',
    };

    if (sectionMode === SectionMode.BOTH) {
      if (parts.length !== 8) throw invalidKey(key);
      const menMin = parseRangePart(parts[4], key);
      const menMax = parseRangePart(parts[5], key);
      const womenMin = parseRangePart(parts[6], key);
      const womenMax = parseRangePart(parts[7], key);
      assertRange(menMin, menMax, 'capacityMen');
      assertRange(womenMin, womenMax, 'capacityWomen');
      return {
        ...identity,
        capacityMin: menMin + womenMin,
        capacityMax: menMax + womenMax,
        capacityMenMin: menMin,
        capacityMenMax: menMax,
        capacityWomenMin: womenMin,
        capacityWomenMax: womenMax,
      };
    }

    if (parts.length !== 6) throw invalidKey(key);
    const capacityMin = parseRangePart(parts[4], key);
    const capacityMax = parseRangePart(parts[5], key);
    assertRange(capacityMin, capacityMax, 'capacity');
    return { ...identity, capacityMin, capacityMax };
  }

  static fromString(value: string): PackageKey {
    PackageKey.parse(value);
    return new PackageKey(value);
  }

  toString(): string {
    return this.value;
  }

  equals(other: PackageKey): boolean {
    return this.value === other.value;
  }
}

function requireRangePart(value: number | undefined, field: string): number {
  if (value === undefined || !Number.isInteger(value) || value < 1) {
    throw new ValidationError(`${field} يجب أن يكون عدداً صحيحاً موجباً`, {
      field,
      value,
    });
  }
  return value;
}

function parseRangePart(value: string | undefined, key: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) throw invalidKey(key);
  return parsed;
}

function assertRange(minimum: number, maximum: number, field: string): void {
  requireRangePart(minimum, `${field}Min`);
  requireRangePart(maximum, `${field}Max`);
  if (minimum > maximum) {
    throw new ValidationError(`${field}Min يجب ألا يتجاوز ${field}Max`, {
      field: `${field}Min`,
      value: minimum,
    });
  }
}

function invalidKey(key: string): ValidationError {
  return new ValidationError(`صيغة مفتاح الباقة غير صالحة: ${key}`, {
    field: 'packageKey',
    value: key,
  });
}
